/**
 * Pure tree traversal and manipulation utilities for nested sections.
 *
 * All functions are generic over `T` so they work with both
 * `SectionEnvelope` (shared) and `EditorSection` (zvgnext).
 * All operations are immutable — they return new arrays.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Constraint for any section-like object with optional editorId and children. */
export interface TreeNode {
  _editorId?: string
  children?: TreeNode[]
}

/** Result of looking up a section by editorId. */
export interface SectionLookup<T> {
  section: T
  /** Index path from root, e.g. [2, 0] = root[2].children[0] */
  path: number[]
  /** Parent section, or null for root-level sections */
  parent: T | null
  /** Nesting depth (0 = root) */
  depth: number
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Inline array-move (no @dnd-kit dependency). Removes at oldIndex, inserts at newIndex. */
export function moveItem<T>(arr: T[], oldIndex: number, newIndex: number): T[] {
  if (oldIndex === newIndex) return arr
  const result = [...arr]
  const [item] = result.splice(oldIndex, 1)
  result.splice(newIndex, 0, item)
  return result
}

// ---------------------------------------------------------------------------
// Find
// ---------------------------------------------------------------------------

/**
 * Find a section by `_editorId` at any depth.
 * Returns the section, its index path, parent, and depth — or null if not found.
 */
export function findSectionByEditorId<T extends TreeNode>(
  sections: T[],
  editorId: string,
  _parent: T | null = null,
  _basePath: number[] = [],
  _depth: number = 0,
): SectionLookup<T> | null {
  for (let i = 0; i < sections.length; i++) {
    const s = sections[i]
    const path = [..._basePath, i]
    if (s._editorId === editorId) {
      return { section: s, path, parent: _parent, depth: _depth }
    }
    if (s.children && s.children.length > 0) {
      const found = findSectionByEditorId(
        s.children as T[],
        editorId,
        s,
        path,
        _depth + 1,
      )
      if (found) return found
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Remove
// ---------------------------------------------------------------------------

/**
 * Remove a section by `_editorId` at any depth. Returns a new array.
 * If not found, returns the original array reference.
 */
export function removeSectionByEditorId<T extends TreeNode>(
  sections: T[],
  editorId: string,
): T[] {
  let changed = false
  const result = sections.reduce<T[]>((acc, s) => {
    if (s._editorId === editorId) {
      changed = true
      return acc // skip this section
    }
    if (s.children && s.children.length > 0) {
      const newChildren = removeSectionByEditorId(s.children as T[], editorId)
      if (newChildren !== s.children) {
        changed = true
        acc.push({
          ...s,
          children: newChildren.length > 0 ? newChildren : undefined,
        } as T)
        return acc
      }
    }
    acc.push(s)
    return acc
  }, [])
  return changed ? result : sections
}

// ---------------------------------------------------------------------------
// Add child
// ---------------------------------------------------------------------------

/**
 * Add a child to a container identified by `parentEditorId`.
 * The child is appended to the end of the parent's children array.
 * If parent not found, returns the original array reference.
 */
export function addChildToSection<T extends TreeNode>(
  sections: T[],
  parentEditorId: string,
  child: T,
): T[] {
  let changed = false
  const result = sections.map((s) => {
    if (s._editorId === parentEditorId) {
      changed = true
      return {
        ...s,
        children: [...((s.children as T[]) ?? []), child],
      } as T
    }
    if (s.children && s.children.length > 0) {
      const newChildren = addChildToSection(s.children as T[], parentEditorId, child)
      if (newChildren !== s.children) {
        changed = true
        return { ...s, children: newChildren } as T
      }
    }
    return s
  })
  return changed ? result : sections
}

// ---------------------------------------------------------------------------
// Reorder children
// ---------------------------------------------------------------------------

/**
 * Reorder children within a specific parent container.
 * `activeId` and `overId` are editorIds of sibling children.
 * If parent not found or IDs don't match, returns original array.
 */
export function reorderChildrenInSection<T extends TreeNode>(
  sections: T[],
  parentEditorId: string,
  activeId: string,
  overId: string,
): T[] {
  let changed = false
  const result = sections.map((s) => {
    if (s._editorId === parentEditorId && s.children) {
      const children = s.children as T[]
      const oldIndex = children.findIndex((c) => c._editorId === activeId)
      const newIndex = children.findIndex((c) => c._editorId === overId)
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return s
      changed = true
      return { ...s, children: moveItem(children, oldIndex, newIndex) } as T
    }
    if (s.children && s.children.length > 0) {
      const newChildren = reorderChildrenInSection(
        s.children as T[],
        parentEditorId,
        activeId,
        overId,
      )
      if (newChildren !== s.children) {
        changed = true
        return { ...s, children: newChildren } as T
      }
    }
    return s
  })
  return changed ? result : sections
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

/**
 * Update a section at any depth using an updater function.
 * Returns new array if found, original array reference if not.
 */
export function updateSectionByEditorId<T extends TreeNode>(
  sections: T[],
  editorId: string,
  updater: (section: T) => T,
): T[] {
  let changed = false
  const result = sections.map((s) => {
    if (s._editorId === editorId) {
      changed = true
      return updater(s)
    }
    if (s.children && s.children.length > 0) {
      const newChildren = updateSectionByEditorId(s.children as T[], editorId, updater)
      if (newChildren !== s.children) {
        changed = true
        return { ...s, children: newChildren } as T
      }
    }
    return s
  })
  return changed ? result : sections
}

// ---------------------------------------------------------------------------
// Depth
// ---------------------------------------------------------------------------

/**
 * Get the nesting depth of a section (0 = root). Returns -1 if not found.
 */
export function getSectionDepth<T extends TreeNode>(
  sections: T[],
  editorId: string,
): number {
  const lookup = findSectionByEditorId(sections, editorId)
  return lookup ? lookup.depth : -1
}
